import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as StateIcon,
  CheckCircle as CheckIcon,
  PendingActions as PendingIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Payment as PaidIcon,
} from '@mui/icons-material';

interface BillingState {
  id: string;
  code: string;
  name: string;
  description: string;
  stateType: 'draft' | 'pending' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  color: string;
  allowEdit: boolean;
  allowCancel: boolean;
  status: 'active' | 'inactive';
  sortOrder: number;
  createdAt: string;
}

const mockStates: BillingState[] = [
  {
    id: '1',
    code: 'DRAFT',
    name: 'Rascunho',
    description: 'Fatura em modo rascunho, ainda nao enviada',
    stateType: 'draft',
    color: '#9E9E9E',
    allowEdit: true,
    allowCancel: true,
    status: 'active',
    sortOrder: 1,
    createdAt: '2024-01-01',
  },
  {
    id: '2',
    code: 'PENDING',
    name: 'Pendente',
    description: 'Fatura pendente de aprovacao',
    stateType: 'pending',
    color: '#FF9800',
    allowEdit: true,
    allowCancel: true,
    status: 'active',
    sortOrder: 2,
    createdAt: '2024-01-01',
  },
  {
    id: '3',
    code: 'SENT',
    name: 'Enviada',
    description: 'Fatura enviada ao cliente',
    stateType: 'sent',
    color: '#2196F3',
    allowEdit: false,
    allowCancel: true,
    status: 'active',
    sortOrder: 3,
    createdAt: '2024-01-01',
  },
  {
    id: '4',
    code: 'PAID',
    name: 'Paga',
    description: 'Fatura totalmente paga',
    stateType: 'paid',
    color: '#4CAF50',
    allowEdit: false,
    allowCancel: false,
    status: 'active',
    sortOrder: 4,
    createdAt: '2024-01-01',
  },
  {
    id: '5',
    code: 'OVERDUE',
    name: 'Vencida',
    description: 'Fatura com pagamento em atraso',
    stateType: 'overdue',
    color: '#F44336',
    allowEdit: false,
    allowCancel: true,
    status: 'active',
    sortOrder: 5,
    createdAt: '2024-01-01',
  },
  {
    id: '6',
    code: 'CANCELLED',
    name: 'Cancelada',
    description: 'Fatura cancelada',
    stateType: 'cancelled',
    color: '#000000',
    allowEdit: false,
    allowCancel: false,
    status: 'active',
    sortOrder: 6,
    createdAt: '2024-01-01',
  },
];

const StatesTab: React.FC = () => {
  const [states, setStates] = useState<BillingState[]>(mockStates);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedState, setSelectedState] = useState<BillingState | null>(null);
  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    description: string;
    stateType: 'draft' | 'pending' | 'sent' | 'paid' | 'overdue' | 'cancelled';
    color: string;
    allowEdit: boolean;
    allowCancel: boolean;
    status: 'active' | 'inactive';
    sortOrder: number;
  }>({
    code: '',
    name: '',
    description: '',
    stateType: 'draft',
    color: '#9E9E9E',
    allowEdit: true,
    allowCancel: true,
    status: 'active',
    sortOrder: 1,
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'draft':
        return <StateIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'sent':
        return <SendIcon fontSize="small" />;
      case 'paid':
        return <PaidIcon fontSize="small" />;
      case 'overdue':
        return <CancelIcon fontSize="small" />;
      case 'cancelled':
        return <CancelIcon fontSize="small" />;
      default:
        return <StateIcon fontSize="small" />;
    }
  };

  const getTypeChip = (type: string, color: string) => {
    const labels: Record<string, string> = {
      draft: 'Rascunho',
      pending: 'Pendente',
      sent: 'Enviada',
      paid: 'Paga',
      overdue: 'Vencida',
      cancelled: 'Cancelada',
    };
    return (
      <Chip size="small" label={labels[type] || type} icon={getTypeIcon(type)} sx={{ bgcolor: color, color: 'white' }} />
    );
  };

  const getStatusChip = (status: string) => {
    return status === 'active' ? (
      <Chip size="small" color="success" label="Activo" icon={<CheckIcon />} />
    ) : (
      <Chip size="small" color="default" label="Inactivo" icon={<CancelIcon />} />
    );
  };

  const filteredStates = states.filter((state) => {
    const matchesSearch =
      searchTerm === '' ||
      state.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      state.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      state.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || state.stateType === typeFilter;
    const matchesStatus = statusFilter === 'all' || state.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    total: states.length,
    active: states.filter((s) => s.status === 'active').length,
    editable: states.filter((s) => s.allowEdit).length,
    cancellable: states.filter((s) => s.allowCancel).length,
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEdit = (state?: BillingState) => {
    if (state) {
      setSelectedState(state);
      setFormData({
        code: state.code,
        name: state.name,
        description: state.description,
        stateType: state.stateType,
        color: state.color,
        allowEdit: state.allowEdit,
        allowCancel: state.allowCancel,
        status: state.status,
        sortOrder: state.sortOrder,
      });
    } else {
      setSelectedState(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        stateType: 'draft',
        color: '#9E9E9E',
        allowEdit: true,
        allowCancel: true,
        status: 'active',
        sortOrder: states.length + 1,
      });
    }
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setSelectedState(null);
  };

  const handleSave = () => {
    if (selectedState) {
      setStates(states.map((s) => (s.id === selectedState.id ? { ...s, ...formData } : s)));
    } else {
      const newState: BillingState = {
        id: crypto.randomUUID(),
        ...formData,
        createdAt: new Date().toISOString().split('T')[0],
      };
      setStates([...states, newState]);
    }
    handleCloseEdit();
  };

  const handleDelete = (state: BillingState) => {
    if (window.confirm(`Tem certeza que deseja eliminar o estado "${state.name}"?`)) {
      setStates(states.filter((s) => s.id !== state.id));
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={500}>
          Estados de Faturacao
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenEdit()}>
          Novo Estado
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StateIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Estados
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.active}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Activos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.editable}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Editaveis
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CancelIcon color="warning" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.cancellable}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cancelaveis
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar estados..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Tipo"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="sent">Enviada</MenuItem>
                <MenuItem value="paid">Paga</MenuItem>
                <MenuItem value="overdue">Vencida</MenuItem>
                <MenuItem value="cancelled">Cancelada</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* States Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={100}>Codigo</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Descricao</TableCell>
                <TableCell width={130}>Tipo</TableCell>
                <TableCell width={80} align="center">
                  Ordem
                </TableCell>
                <TableCell width={100} align="center">
                  Editavel
                </TableCell>
                <TableCell width={100} align="center">
                  Cancelavel
                </TableCell>
                <TableCell width={100}>Estado</TableCell>
                <TableCell width={100} align="center">
                  Accoes
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStates
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((state) => (
                  <TableRow key={state.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                        {state.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {state.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {state.description}
                      </Typography>
                    </TableCell>
                    <TableCell>{getTypeChip(state.stateType, state.color)}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={state.sortOrder} variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      {state.allowEdit ? <CheckIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
                    </TableCell>
                    <TableCell align="center">
                      {state.allowCancel ? <CheckIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
                    </TableCell>
                    <TableCell>{getStatusChip(state.status)}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEdit(state)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(state)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredStates.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StateIcon />
          {selectedState ? 'Editar Estado' : 'Novo Estado'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Codigo"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 20 }}
                disabled={!!selectedState}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Tipo"
                value={formData.stateType}
                onChange={(e) => setFormData({ ...formData, stateType: e.target.value as any })}
              >
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="sent">Enviada</MenuItem>
                <MenuItem value="paid">Paga</MenuItem>
                <MenuItem value="overdue">Vencida</MenuItem>
                <MenuItem value="cancelled">Cancelada</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="color"
                label="Cor"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descricao"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Ordem de Exibicao"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Permite Edicao"
                value={formData.allowEdit ? 'yes' : 'no'}
                onChange={(e) => setFormData({ ...formData, allowEdit: e.target.value === 'yes' })}
              >
                <MenuItem value="yes">Sim</MenuItem>
                <MenuItem value="no">Nao</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Permite Cancelar"
                value={formData.allowCancel ? 'yes' : 'no'}
                onChange={(e) => setFormData({ ...formData, allowCancel: e.target.value === 'yes' })}
              >
                <MenuItem value="yes">Sim</MenuItem>
                <MenuItem value="no">Nao</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={!formData.code || !formData.name}>
            {selectedState ? 'Guardar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StatesTab;
